<script>
import Layout from "../../layouts/main";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import axios from "axios";
import storageService from "@/services/storage.service";
import statusOptionsMixin from "@/mixins/statusOptions";
import DecisionCards from "@/components/shared/DecisionCards.vue";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatusChip from "@/components/shared/StatusChip.vue";
import TableFooter from "@/components/shared/TableFooter.vue";

export default {
  name: "SingleApplicant",
  mixins: [statusOptionsMixin],
  components: {
    DecisionCards,
    Layout,
    EasyDataTable,
    AppIcon,
    StatusChip,
    TableFooter,
  },
  data() {
    return {
      Name: "N/A",
      Status: "N/A",
      Branch: "N/A",
      PhoneNumber: "N/A",
      Email: "N/A",
      customDateLog: "N/A",
      isLoading: false,
      headers: [
        { text: "Date", value: "dateLog" },
        { text: "Activity", value: "appactivity" },
      ],
      arrItem: [],

      // status update dialog
      actionOpen: false,
      actionSelected: "",
      actionComment: "",
      actionError: "",
      actionSubmitting: false,

      // presentation only: client-side paging for the footer
      page: 1,
      rowsPerPage: 10,
    };
  },

  computed: {
    canTakeAction() {
      return this.allowedStatusOptions(this.Status).length > 0;
    },
    initials() {
      const letters = String(this.Name === "N/A" ? "" : this.Name)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");
      return letters || "?";
    },
    pagedItems() {
      const start = (this.page - 1) * this.rowsPerPage;
      return this.arrItem.slice(start, start + this.rowsPerPage);
    },
  },

  mounted() {
    this.showdata();
  },

  methods: {
    openAction() {
      this.actionSelected = "";
      this.actionComment = "";
      this.actionError = "";
      this.actionOpen = true;
    },
    submitAction() {
      if (!this.actionSelected) return;
      const id = this.$route.params.id;
      const aid = isNaN(Number(id)) ? id : Number(id);
      this.actionSubmitting = true;
      this.actionError = "";
      axios
        .post("/applications/updateappstatus/", {
          access_token: storageService.getItem("accessToken"),
          action: this.actionSelected,
          aid: aid,
          comment: this.actionComment,
        })
        .then((response) => {
          if (response.data.status === "SUCCESS") {
            this.actionOpen = false;
            this.showdata();
          }
        })
        .catch((error) => {
          this.actionError =
            (error.response && error.response.data && error.response.data.error) ||
            "Could not update the status.";
        })
        .finally(() => {
          this.actionSubmitting = false;
        });
    },
    onPageChange(page) {
      this.page = page;
    },
    onRowsChange(rowsPerPage) {
      this.rowsPerPage = rowsPerPage;
      this.page = 1;
    },
    showdata() {
      const token = storageService.getItem("accessToken");
      const app_id = this.$route.params.id;

      if (!app_id) {
        console.error("No app_id found in route params");
        return;
      }

      this.isLoading = true;

      axios
        .post("/applications/single_applicant/", {
          token: token,
          app_id: app_id,
        })
        .then((response) => {
          const data = response.data?.applicant_details;
          if (!data) {
            console.error("No applicant_details in response");
            return;
          }

          const a1 =
            data && typeof data.section_a1 === "object" && data.section_a1 !== null
              ? data.section_a1
              : {};
          const applicant = (data && data.applicant) || {};

          this.Name =
            a1.first_name || a1.sur_name
              ? `${a1.first_name || ""} ${a1.sur_name || ""}`.trim()
              : data.applicant_name ||
                `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim() ||
                applicant.username ||
                "N/A";
          this.Status = data.status || "N/A";
          this.Branch = a1.branch || applicant.branch_name || "N/A";
          this.PhoneNumber =
            a1.home_contact || a1.mob_contact || applicant.mob_no || "N/A";
          this.Email = a1.email || applicant.email || "N/A";
          this.customDateLog = data.created_on
            ? data.created_on.slice(0, 10)
            : "N/A";

          const responseList = Array.isArray(response.data?.app_notifications)
            ? response.data.app_notifications
            : [];

          const arrList = responseList.map((item) => ({
            dateLog: new Date(item.created_on).toISOString().slice(0, 10),
            appactivity: item.content,
          }));

          this.arrItem = arrList;
        })
        .catch((error) => {
          console.error("API error:", error);
          this.arrItem = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <Layout>
    <section class="sapp">
      <header class="ecu-page-head">
        <div>
          <span class="ecu-page-head__bar"></span>
          <h1>Single Application</h1>
          <p class="ecu-page-head__sub">
            Applicant details and the activity recorded on this application.
          </p>
        </div>
        <router-link to="/applicants" class="ecu-text-link">
          <AppIcon name="chevron-left" :size="16" :stroke="2.2" />
          All Applications
        </router-link>
      </header>

      <div class="ecu-detail">
        <!-- application info -->
        <aside class="ecu-card">
          <div class="ecu-card__head">
            <span class="ecu-tile"><AppIcon name="id-card" :size="20" /></span>
            <h2 class="ecu-card__title">Application Info</h2>
          </div>

          <div class="ecu-profile">
            <span class="ecu-profile__avatar">{{ initials }}</span>
            <h3 class="ecu-profile__name">
              <span
                v-if="isLoading"
                class="ecu-skeleton ecu-info__sk"
                aria-hidden="true"
              ></span>
              <template v-else>{{ Name }}</template>
            </h3>
            <span class="ecu-profile__role">Applicant</span>
          </div>

          <ul class="ecu-info">
            <li class="ecu-info__item">
              <span class="ecu-info__icon"><AppIcon name="mail" :size="18" /></span>
              <span class="ecu-info__text">
                <span class="ecu-info__label">Email</span>
                <span class="ecu-info__value">
                  <span v-if="isLoading" class="ecu-skeleton ecu-info__sk"></span>
                  <template v-else>{{ Email }}</template>
                </span>
              </span>
            </li>
            <li class="ecu-info__item">
              <span class="ecu-info__icon"><AppIcon name="phone" :size="18" /></span>
              <span class="ecu-info__text">
                <span class="ecu-info__label">Phone Number</span>
                <span class="ecu-info__value">
                  <span v-if="isLoading" class="ecu-skeleton ecu-info__sk"></span>
                  <template v-else>{{ PhoneNumber }}</template>
                </span>
              </span>
            </li>
            <li class="ecu-info__item">
              <span class="ecu-info__icon">
                <AppIcon name="shield-check" :size="18" />
              </span>
              <span class="ecu-info__text">
                <span class="ecu-info__label">Status</span>
                <span class="ecu-info__value">
                  <span v-if="isLoading" class="ecu-skeleton ecu-info__sk"></span>
                  <StatusChip v-else :status="Status" />
                </span>
              </span>
            </li>
            <li class="ecu-info__item">
              <span class="ecu-info__icon"><AppIcon name="bank" :size="18" /></span>
              <span class="ecu-info__text">
                <span class="ecu-info__label">Branch</span>
                <span class="ecu-info__value">
                  <span v-if="isLoading" class="ecu-skeleton ecu-info__sk"></span>
                  <template v-else>{{ Branch }}</template>
                </span>
              </span>
            </li>
            <li class="ecu-info__item">
              <span class="ecu-info__icon">
                <AppIcon name="calendar" :size="18" />
              </span>
              <span class="ecu-info__text">
                <span class="ecu-info__label">Date</span>
                <span class="ecu-info__value">
                  <span v-if="isLoading" class="ecu-skeleton ecu-info__sk"></span>
                  <template v-else>{{ customDateLog }}</template>
                </span>
              </span>
            </li>
          </ul>
        </aside>

        <!-- activity -->
        <div class="ecu-card">
          <div class="ecu-card__head">
            <span class="ecu-tile"><AppIcon name="list" :size="20" /></span>
            <h2 class="ecu-card__title">Application Activity</h2>
          </div>

          <div class="ecu-table-wrap">
            <EasyDataTable
              header-text-direction="center"
              :headers="headers"
              :items="pagedItems"
              :loading="isLoading"
              :rows-per-page="rowsPerPage"
              empty-message="No activity recorded yet"
              theme-color="#f97316"
              hide-footer
              table-class-name="table-custom-style"
            >
              <template #item-dateLog="{ dateLog }">
                <span class="ecu-date">
                  <AppIcon name="calendar" :size="16" />
                  {{ dateLog }}
                </span>
              </template>
              <template #item-appactivity="{ appactivity }">
                <span class="sapp__activity">{{ appactivity }}</span>
              </template>
            </EasyDataTable>
          </div>

          <TableFooter
            :page="page"
            :rows-per-page="rowsPerPage"
            :total="arrItem.length"
            :loading="isLoading"
            @update:page="onPageChange"
            @update:rowsPerPage="onRowsChange"
          />
        </div>
      </div>

      <!-- full application + status actions -->
      <div class="ecu-card sapp__review">
        <div class="sapp__review-text">
          <span class="ecu-tile"><AppIcon name="file" :size="20" /></span>
          <div>
            <h2 class="ecu-card__title">Application</h2>
            <p>Open the submitted form to read every section, or update its status.</p>
          </div>
        </div>
        <div class="sapp__review-actions">
          <router-link :to="`/view_application/${$route.params.id}`" class="sapp__btn sapp__btn--ghost">
            <AppIcon name="eye" :size="16" /> View Application
          </router-link>
          <button
            type="button"
            class="sapp__btn sapp__btn--primary"
            :disabled="isLoading || !canTakeAction"
            :title="canTakeAction ? '' : 'No further status changes are available'"
            @click="openAction"
          >
            <AppIcon name="edit" :size="16" /> Update Status
          </button>
        </div>
      </div>

      <b-modal v-model="actionOpen" title="Update Application Status" hide-footer centered>
        <p class="sapp__meta">Applicant: <strong>{{ Name }}</strong></p>

        <label class="ecu-m-label">Choose an action</label>
        <DecisionCards v-model="actionSelected" :options="allowedStatusOptions(Status)" />

        <div
          v-if="actionSelected === 'rejected' || actionSelected === 'incomplete'"
          class="ecu-m-field sapp__gap"
        >
          <label class="ecu-m-label">Comment</label>
          <textarea
            v-model="actionComment"
            class="ecu-m-input ecu-m-textarea"
            rows="3"
            placeholder="Reason or notes regarding this status change..."
          ></textarea>
        </div>

        <p v-if="actionError" class="ecu-m-alert ecu-m-alert--error sapp__gap">
          <AppIcon name="alert" :size="18" /> {{ actionError }}
        </p>

        <div class="ecu-m-actions">
          <button
            type="button"
            class="ecu-m-btn ecu-m-btn--ghost"
            :disabled="actionSubmitting"
            @click="actionOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ecu-m-btn"
            :disabled="!actionSelected || actionSubmitting"
            @click="submitAction"
          >
            <span v-if="actionSubmitting">Saving...</span>
            <span v-else>Save Status</span>
          </button>
        </div>
      </b-modal>
    </section>
  </Layout>
</template>

<style scoped>
.sapp {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.sapp .ecu-page-head {
  margin-bottom: 0;
}
.sapp .ecu-card {
  padding: 22px 22px 18px;
}
.sapp .ecu-card__head .ecu-tile {
  background: var(--ecu-orange-soft, #fff1e6);
}
.sapp__review {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px 24px;
}
.sapp__review-text {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.sapp__review-text p {
  margin: 2px 0 0;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-muted, #5f6579);
}
.sapp__review-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.sapp__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 42px;
  padding: 0 20px;
  border: 1.5px solid transparent;
  border-radius: 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease,
    border-color 0.18s ease, color 0.18s ease;
}
.sapp__btn--ghost {
  background: #fff;
  border-color: #cfd3e0;
  color: var(--ecu-navy, #0b1f4b);
}
.sapp__btn--ghost:hover {
  border-color: var(--ecu-orange, #f97316);
  color: var(--ecu-orange-dark, #e84d00);
  background: var(--ecu-orange-soft, #fff1e6);
}
.sapp__btn--primary {
  background: linear-gradient(135deg, #ff7a1a, #f2610f);
  color: #fff;
  box-shadow: 0 8px 20px rgba(242, 97, 15, 0.28);
}
.sapp__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(242, 97, 15, 0.34);
}
.sapp__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}
.sapp__meta {
  margin: 0 0 14px;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-muted, #5f6579);
}
.sapp__meta strong {
  color: var(--ecu-navy, #0b1f4b);
}
.sapp__gap {
  margin-top: 16px;
}
.sapp__activity {
  display: inline-block;
  line-height: 1.45;
  white-space: normal;
}
@media (max-width: 767px) {
  .sapp .ecu-card {
    padding: 16px 14px 14px;
  }
  .sapp__review-actions,
  .sapp__btn {
    width: 100%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .sapp__btn { transition: none; }
}
</style>